"use client";

import React, { useState } from "react";
import { ChevronRight, ChevronLeft, Check, ShieldCheck, Activity, MessageSquare } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
const STEPS = [{
  id: 1,
  title: "Athlete Profile",
  icon: ShieldCheck
}, {
  id: 2,
  title: "Gear Specs",
  icon: Activity
}, {
  id: 3,
  title: "Consultation Details",
  icon: MessageSquare
}];
export default function ExpertConsultation() {
  return <section id="expert-consultation" className="dark bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-3xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tighter mb-4">
            <span className="text-primary">Expert</span> Consultation
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Get personalized advice from our performance specialists. Tell us about your needs, and we'll guide you to the perfect fit.
          </p>
        </div>

        <div className="rounded-xl border border-secondary/50 bg-card/50 backdrop-blur-md shadow-[0_0_30px_-10px_var(--color-secondary)] overflow-hidden">
          <div className="p-1 bg-gradient-to-r from-transparent via-secondary/50 to-transparent opacity-50" />
          
          <FormProvider formId="expert_consultation_shoe_tech_size_guide" sectionName="expert-consultation" className="p-6 md:p-8" defaultValues={{
          name: "",
          email: "",
          phone: "",
          shoe_model: "",
          consultation_topic: "",
          message: "",
          preferred_contact_time: "No Preference"
        }} validationRules={{
          name: {
            required: "Name is required"
          },
          email: {
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          },
          consultation_topic: {
            required: "Please select a topic"
          },
          message: {
            required: "Please provide details",
            minLength: {
              value: 10,
              message: "Please provide at least 10 characters"
            }
          }
        }} submitText="Request Consultation" successMessage="Request received! Our experts will analyze your profile and contact you shortly.">
            <MultiStepForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function MultiStepForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    trigger
  } = useWvcForm();
  const [currentStep, setCurrentStep] = useState(1);
  const progress = currentStep / STEPS.length * 100;
  const nextStep = async () => {
    let isValid = false;
    if (currentStep === 1) {
      isValid = await trigger(["name", "email", "phone"]);
    } else if (currentStep === 2) {
      isValid = await trigger(["shoe_model", "consultation_topic"]);
    }
    if (isValid) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };
  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center border border-primary shadow-[0_0_20px_-5px_var(--color-primary)]">
          <Check className="w-10 h-10 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">Request Confirmed</h3>
          <p className="text-muted-foreground max-w-md">{successMessage}</p>
        </div>
        <Button variant="outline" className="mt-4 border-primary/50 text-primary hover:bg-primary/10" onClick={() => window.location.reload()}>
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <div className="space-y-4">
        <div className="flex justify-between text-sm font-medium text-muted-foreground">
          {STEPS.map(step => <div key={step.id} className={`flex items-center gap-2 ${currentStep >= step.id ? "text-primary" : ""}`}>
              <div className={`p-1.5 rounded-full border ${currentStep >= step.id ? "border-primary bg-primary/10" : "border-muted bg-muted/10"}`}>
                <step.icon className="w-4 h-4" />
              </div>
              <span className="hidden md:inline">{step.title}</span>
            </div>)}
        </div>
        <Progress value={progress} className="h-1 bg-muted" />
      </div>

      {}
      {currentStep === 1 && <div className="space-y-6 animate-in slide-in-from-right-4 fade-in duration-300">
          <div className="grid gap-6 md:grid-cols-2">
            <FormField control={control} name="name" render={({
          field
        }) => <FormItem>
                  <FormLabel>Your Name</FormLabel>
                  <FormControl>
                    <input className="flex h-12 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all focus:border-primary/50" placeholder="Jane Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <input type="email" className="flex h-12 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all focus:border-primary/50" placeholder="jane@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
          <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                <FormLabel>Phone Number <span className="text-muted-foreground text-xs ml-1">(Optional)</span></FormLabel>
                <FormControl>
                  <input type="tel" className="flex h-12 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all focus:border-primary/50" placeholder="+1 555-123-4567" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>}

      {}
      {currentStep === 2 && <div className="space-y-6 animate-in slide-in-from-right-4 fade-in duration-300">
          <FormField control={control} name="shoe_model" render={({
        field
      }) => <FormItem>
                <FormLabel>Shoe Model <span className="text-muted-foreground text-xs ml-1">(If known)</span></FormLabel>
                <FormControl>
                  <input className="flex h-12 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all focus:border-primary/50" placeholder="e.g., Nike Air Zoom Pegasus 40" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="consultation_topic" render={({
        field
      }) => <FormItem>
                <FormLabel>Consultation Topic</FormLabel>
                <FormControl>
                  <select className="flex h-12 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all focus:border-primary/50 appearance-none" {...field}>
                    <option value="" disabled>Select a topic</option>
                    <option value="Fit & Sizing">Fit & Sizing</option>
                    <option value="Performance Technology">Performance Technology</option>
                    <option value="Material & Durability">Material & Durability</option>
                    <option value="Custom Recommendations">Custom Recommendations</option>
                    <option value="Other">Other</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>}

      {}
      {currentStep === 3 && <div className="space-y-6 animate-in slide-in-from-right-4 fade-in duration-300">
          <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
                <FormLabel>Additional Details</FormLabel>
                <FormControl>
                  <textarea className="flex min-h-[120px] w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all focus:border-primary/50 resize-y" placeholder="Provide any specific questions or concerns you have..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="preferred_contact_time" render={({
        field
      }) => <FormItem>
                <FormLabel>Preferred Contact Time</FormLabel>
                <FormControl>
                  <select className="flex h-12 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all focus:border-primary/50 appearance-none" {...field}>
                    <option value="No Preference">No Preference</option>
                    <option value="Morning (8am-12pm)">Morning (8am-12pm)</option>
                    <option value="Afternoon (12pm-5pm)">Afternoon (12pm-5pm)</option>
                    <option value="Evening (5pm-9pm)">Evening (5pm-9pm)</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>}

      {}
      <div className="flex justify-between pt-4 border-t border-border/50">
        <Button type="button" variant="ghost" onClick={prevStep} disabled={currentStep === 1 || isSubmitting} className={`text-muted-foreground hover:text-foreground ${currentStep === 1 ? "invisible" : ""}`}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {currentStep < STEPS.length ? <Button type="button" onClick={nextStep} className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-[0_0_15px_-5px_var(--color-secondary)]">
            Next Step
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button> : <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 w-full md:w-auto min-w-[200px] shadow-[0_0_20px_-5px_var(--color-primary)] font-bold tracking-wide">
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </Button>}
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm text-center animate-in fade-in">
          {submitError}
        </div>}
    </Form>;
}